import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import { formatCurrency } from 'tio-ui/format';
import Chip from 'tio-ui/components/chip';
import Button from 'tio-ui/components/button';
import Card from 'tio-ui/components/card';
import { and, not } from 'tio-ui/utilities';
import PageHero from 'tio-ui/components/page-hero';
import {
  PhCompass,
  PhShield,
  PhWarning,
  PhCheckCircle,
  PhX,
  PhArrowRight,
  PhLightbulb,
  PhCurrencyDollar,
  PhScales,
  PhTarget,
  PhBank,
  PhBuildings,
  PhMagnifyingGlass,
  PhHandshake,
  PhRocket,
  PhSparkle,
  PhInfo,
  PhChartLineUp,
  PhSealCheck,
  PhProhibit,
} from 'ember-phosphor-icons';
import {
  getCurrentDemoClientData,
  getCurrentDemoScenario,
  getCurrentDemoClientId,
} from 'tio-employee/utils/career-pathways-demo-data';
import type CareerPathwaysApiService from 'tio-employee/services/career-pathways-api';
import type { LrapDiscoverySummary } from 'tio-employee/services/career-pathways-api';

interface RepaymentStrategySignature {
  Args: {
    model: unknown;
  };
}

// Repayment strategy recommendation types
interface StrategyPath {
  id: string;
  name: string;
  description: string;
  icon: typeof PhTarget;
  benefits: string[];
  warnings: string[];
  monthlyPayment: number;
  totalPaid: number;
  timeToFreedom: string;
  forgivenessAmount: number;
  outOfPocket: number;
  isRecommended: boolean;
  reason?: string;
}

interface StrategyWarning {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  icon: typeof PhWarning;
  title: string;
  description: string;
  learnMoreLink?: string;
}

interface StrategyFactor {
  id: string;
  label: string;
  value: string;
  impact: 'positive' | 'negative' | 'neutral';
  icon: typeof PhCheckCircle;
}

export default class RepaymentStrategyIndex extends Component<RepaymentStrategySignature> {
  @service('career-pathways-api') declare careerPathwaysApi: CareerPathwaysApiService;

  // Wizard state
  @tracked currentStep = 1;
  @tracked selectedGoal: string = '';
  @tracked employmentType: string = '';
  @tracked isPublicService: boolean = false;
  @tracked yearsInPublicService: number = 0;
  @tracked consideringRefinance: boolean = false;
  @tracked showResults: boolean = false;

  // Demo loan profile
  loanProfile = {
    totalBalance: 68420,
    federalBalance: 52640,
    privateBalance: 15780,
    monthlyIncome: 5200,
    householdSize: 2,
    filingStatus: 'single',
    interestRates: { federal: 5.5, private: 7.8 },
  };

  get demoClient() {
    return getCurrentDemoClientData();
  }

  get scenario() {
    return getCurrentDemoScenario();
  }

  get clientId() {
    return getCurrentDemoClientId();
  }

  get lrapDiscovery(): LrapDiscoverySummary | null {
    if (this.clientId === 'legal' || this.clientId === 'advocate') {
      return this.careerPathwaysApi.fetchLrapDiscoverySyncForDemo();
    }
    return null;
  }

  get hasLrapOpportunity() {
    const discovery = this.lrapDiscovery;
    if (!discovery) return false;
    return discovery.eligiblePrograms.length > 0 || discovery.enrolledPrograms.length > 0;
  }

  get totalLrapValue() {
    return this.lrapDiscovery?.totalPotentialAnnualValue ?? 0;
  }

  // Compute if PSLF is viable
  get isPslfViable() {
    // Demo: based on employment type and years
    return this.isPublicService && this.yearsInPublicService < 8;
  }

  get pslfPaymentsRemaining() {
    if (!this.isPublicService) return 120;
    return Math.max(0, 120 - this.yearsInPublicService * 12);
  }

  get pslfForgivenessAmount() {
    // Simplified: remaining balance after minimum payments
    const monthlyIdPayment = this.calculateIdrPayment();
    const totalPaid = monthlyIdPayment * this.pslfPaymentsRemaining;
    return Math.max(0, this.loanProfile.federalBalance - totalPaid + 15000); // Rough estimate
  }

  calculateIdrPayment() {
    // Simplified IDR calculation: 10% of discretionary income
    const discretionary = Math.max(0, this.loanProfile.monthlyIncome * 12 - 23000) / 12;
    return Math.round(discretionary * 0.1);
  }

  get strategyPaths(): StrategyPath[] {
    const idrPayment = this.calculateIdrPayment();
    const standardPayment = Math.round(this.loanProfile.totalBalance / 120); // 10-year standard

    const paths: StrategyPath[] = [];

    // PSLF Path (if eligible)
    if (this.isPublicService) {
      const pslfMonths = this.pslfPaymentsRemaining;
      const pslfTotalPaid = idrPayment * pslfMonths;

      paths.push({
        id: 'pslf',
        name: 'Public Service Loan Forgiveness',
        description: 'Continue qualifying payments toward 120-payment forgiveness',
        icon: PhBuildings,
        benefits: [
          `${pslfMonths} payments remaining (${Math.round(pslfMonths / 12)} years)`,
          `${formatCurrency(this.pslfForgivenessAmount)} forgiven tax-free`,
          'IDR payments capped at 10% discretionary income',
          'Employer contributions count toward payments',
        ],
        warnings:
          pslfMonths < 36
            ? ['You are close to forgiveness - do not refinance!']
            : ['Must maintain qualifying employment', 'Annual ECF certification recommended'],
        monthlyPayment: idrPayment,
        totalPaid: pslfTotalPaid,
        timeToFreedom: `${Math.round(pslfMonths / 12)} years`,
        forgivenessAmount: this.pslfForgivenessAmount,
        outOfPocket: pslfTotalPaid,
        isRecommended: this.isPslfViable && !this.consideringRefinance,
        reason: 'Your public service employment makes you eligible for significant forgiveness.',
      });
    }

    // IDR Path (for everyone with federal loans)
    paths.push({
      id: 'idr',
      name: 'Income-Driven Repayment (RAP)',
      description: 'Lower monthly payments based on income, forgiveness after 20-25 years',
      icon: PhScales,
      benefits: [
        `${formatCurrency(idrPayment)}/mo (vs ${formatCurrency(standardPayment)} standard)`,
        'Payment adjusts with income changes',
        'Remaining balance forgiven after 20 years',
        this.hasLrapOpportunity ? 'Compatible with LRAP programs!' : 'Employer help reduces burden',
      ],
      warnings: ['Forgiveness is taxable (may have tax bill)', 'Extends repayment period'],
      monthlyPayment: idrPayment,
      totalPaid: idrPayment * 240,
      timeToFreedom: '20 years',
      forgivenessAmount: Math.max(0, this.loanProfile.federalBalance - idrPayment * 240 + 20000),
      outOfPocket: idrPayment * 240,
      isRecommended: !this.isPublicService && this.loanProfile.federalBalance > 40000,
      reason: 'Your income-to-debt ratio makes IDR the most affordable path.',
    });

    const aggressivePayment = Math.round(this.loanProfile.totalBalance / 60); // 5-year
    paths.push({
      id: 'aggressive',
      name: 'Aggressive Payoff',
      description: 'Pay off loans in 5 years with higher monthly payments',
      icon: PhRocket,
      benefits: [
        'Debt-free in 5 years',
        `Save ${formatCurrency(15000)} in interest`,
        'No forgiveness uncertainty',
        'Maximum employer benefit utilization',
      ],
      warnings: [
        `High payment: ${formatCurrency(aggressivePayment)}/mo`,
        'Less financial flexibility',
        this.isPublicService ? 'Loses PSLF forgiveness opportunity' : '',
      ].filter(Boolean),
      monthlyPayment: aggressivePayment,
      totalPaid: aggressivePayment * 60,
      timeToFreedom: '5 years',
      forgivenessAmount: 0,
      outOfPocket: aggressivePayment * 60,
      isRecommended: this.loanProfile.totalBalance < 30000,
    });

    // Refinance Path (with STRONG warnings if PSLF eligible)
    if (this.consideringRefinance) {
      const refiPayment = Math.round((this.loanProfile.totalBalance * 0.045) / 12 + 500);
      paths.push({
        id: 'refinance',
        name: 'Private Refinancing',
        description: 'Consolidate to a private lender at potentially lower rate',
        icon: PhBank,
        benefits: [
          'Potentially lower interest rate',
          'Simplified single payment',
          'Fixed rate option available',
        ],
        warnings: this.getRefinanceWarnings(),
        monthlyPayment: refiPayment,
        totalPaid: refiPayment * 120,
        timeToFreedom: '10 years',
        forgivenessAmount: 0,
        outOfPocket: refiPayment * 120,
        isRecommended: false,
        reason: 'Refinancing has significant drawbacks for your situation.',
      });
    }

    return paths;
  }

  getRefinanceWarnings(): string[] {
    const warnings: string[] = [];

    if (this.isPublicService) {
      warnings.push('⚠️ PERMANENT LOSS: You will forfeit PSLF eligibility forever');
      warnings.push(`You could lose ${formatCurrency(this.pslfForgivenessAmount)} in forgiveness`);
    }

    warnings.push('No access to IDR plans during hardship');
    warnings.push('No federal forbearance or deferment options');
    warnings.push('Cannot use income-driven payments if income drops');

    if (this.hasLrapOpportunity) {
      warnings.push('Some LRAP programs require federal loan status');
    }

    return warnings;
  }

  // Critical warnings to show prominently
  get criticalWarnings(): StrategyWarning[] {
    const warnings: StrategyWarning[] = [];

    // Refinancing warning for PSLF-eligible
    if (this.consideringRefinance && this.isPublicService) {
      warnings.push({
        id: 'refi-pslf',
        severity: 'critical',
        icon: PhProhibit,
        title: 'Refinancing Would Cost You ' + formatCurrency(this.pslfForgivenessAmount),
        description: `As a public service employee with ${this.yearsInPublicService} years of service, refinancing would permanently eliminate your PSLF eligibility. This is almost certainly not in your best interest.`,
        learnMoreLink: 'authenticated.reimagined.pslf-vault',
      });
    }

    // LRAP coordination warning
    if (this.hasLrapOpportunity && this.lrapDiscovery?.outsideFundsConflicts.length) {
      warnings.push({
        id: 'lrap-conflict',
        severity: 'warning',
        icon: PhWarning,
        title: 'Optimize Before Applying',
        description:
          'You have LRAP programs with "outside funds" policies. Employer contributions may reduce your school LRAP benefit dollar-for-dollar unless optimized correctly.',
        learnMoreLink: 'authenticated.reimagined.finance.optimizer',
      });
    }

    return warnings;
  }

  // Key factors influencing recommendation
  get strategyFactors(): StrategyFactor[] {
    return [
      {
        id: 'employment',
        label: 'Employment Type',
        value: this.isPublicService ? 'PSLF-Qualifying Employer' : 'Private Sector',
        impact: this.isPublicService ? 'positive' : 'neutral',
        icon: this.isPublicService ? PhCheckCircle : PhBuildings,
      },
      {
        id: 'loan-type',
        label: 'Federal Loans',
        value: `${formatCurrency(this.loanProfile.federalBalance)} (${Math.round((this.loanProfile.federalBalance / this.loanProfile.totalBalance) * 100)}%)`,
        impact: 'positive',
        icon: PhBank,
      },
      {
        id: 'income-ratio',
        label: 'Debt-to-Income',
        value: `${Math.round((this.loanProfile.totalBalance / (this.loanProfile.monthlyIncome * 12)) * 100)}%`,
        impact:
          this.loanProfile.totalBalance > this.loanProfile.monthlyIncome * 12
            ? 'negative'
            : 'neutral',
        icon: PhScales,
      },
      ...(this.hasLrapOpportunity
        ? [
            {
              id: 'lrap',
              label: 'LRAP Eligible',
              value: `${formatCurrency(this.totalLrapValue)}/year available`,
              impact: 'positive' as const,
              icon: PhMagnifyingGlass,
            },
          ]
        : []),
      ...(this.scenario.features.employerMatch
        ? [
            {
              id: 'employer',
              label: 'Employer Help',
              value: `${formatCurrency(5250)}/year`,
              impact: 'positive' as const,
              icon: PhHandshake,
            },
          ]
        : []),
    ];
  }

  get recommendedPath() {
    return this.strategyPaths.find((p) => p.isRecommended);
  }

  // Wizard navigation
  nextStep = () => {
    if (this.currentStep < 4) {
      this.currentStep++;
    } else {
      this.showResults = true;
    }
  };

  previousStep = () => {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  };

  selectGoal = (goal: string) => {
    this.selectedGoal = goal;
  };

  setPublicService = (value: boolean) => {
    this.isPublicService = value;
    if (!value) {
      this.yearsInPublicService = 0;
    }
  };

  setYearsPublicService = (years: number) => {
    this.yearsInPublicService = years;
  };

  setConsideringRefinance = (value: boolean) => {
    this.consideringRefinance = value;
  };

  resetWizard = () => {
    this.currentStep = 1;
    this.showResults = false;
    this.selectedGoal = '';
    this.employmentType = '';
    this.isPublicService = false;
    this.yearsInPublicService = 0;
    this.consideringRefinance = false;
  };

  // Computed for template use (avoids arithmetic in templates)
  get estimatedQualifyingPayments() {
    return this.yearsInPublicService * 12;
  }

  get pslfProgressPercent() {
    return Math.round((this.yearsInPublicService / 10) * 100);
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="data-audit w-full min-w-0">
      {{#unless this.showResults}}
        <PageHero @gradient="hero">
          <:default>
            <div class="tio-row mb-4">
              <div class="tio-strategy-hero-icon">
                <PhCompass @size={{32}} @weight="duotone" />
              </div>
              <div>
                <Heading @level={{1}} class="tio-fg-inverse">
                  Repayment Strategy Finder
                </Heading>
                {{! Soft on-hero subtitle — inherits --tio-hero-fg at 80% opacity via
                    .tio-hero-subtitle (the recipe set in styles.css). @variant="inherit"
                    prevents Text's default foreground treatment from overriding the hero. }}
                <Text @variant="inherit" class="tio-hero-subtitle">
                  Personalized guidance for your student loan journey
                </Text>
              </div>
            </div>
          </:default>
          <:footer>
            <div class="tio-row-tight">
              {{#each (array 1 2 3 4) as |step|}}
                <div class="tio-row-tight">
                  <div
                    class="w-8 h-8 rounded-full tio-row-center tio-text-sm-medium
                      {{if
                        (eq this.currentStep step)
                        'tio-bg-surface tio-fg-info'
                        (if
                          (lt step this.currentStep)
                          'tio-strategy-step--completed'
                          'tio-strategy-step--pending'
                        )
                      }}"
                  >
                    {{#if (lt step this.currentStep)}}
                      <PhCheckCircle @size={{18}} />
                    {{else}}
                      {{step}}
                    {{/if}}
                  </div>
                  {{#unless (eq step 4)}}
                    <div
                      class="w-12 h-1 rounded
                        {{if
                          (lt step this.currentStep)
                          'tio-strategy-connector--completed'
                          'tio-strategy-connector--pending'
                        }}"
                    ></div>
                  {{/unless}}
                </div>
              {{/each}}
            </div>
          </:footer>
        </PageHero>

        <Card @padding="lg" class="mt-6">
          {{#if (eq this.currentStep 1)}}
            <div>
              <Heading @level={{2}} @size="xl" class="mb-2">
                What's your primary goal?
              </Heading>
              <Text class="tio-fg-secondary mb-6">
                This helps us prioritize what matters most to you.
              </Text>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                {{#each
                  (array
                    (hash
                      id="lowest-payment"
                      title="Lowest Monthly Payment"
                      description="Minimize what I pay each month, even if it takes longer"
                      icon=PhCurrencyDollar
                    )
                    (hash
                      id="fastest-payoff"
                      title="Fastest Debt Freedom"
                      description="Pay off loans as quickly as possible"
                      icon=PhRocket
                    )
                    (hash
                      id="maximize-forgiveness"
                      title="Maximize Forgiveness"
                      description="Minimize total out-of-pocket through PSLF or IDR forgiveness"
                      icon=PhSparkle
                    )
                    (hash
                      id="balanced"
                      title="Balanced Approach"
                      description="Find the sweet spot between payments, speed, and savings"
                      icon=PhScales
                    )
                  )
                  as |goal|
                }}
                  <button
                    type="button"
                    class="p-5 border-2 rounded-xl text-left transition-all
                      {{if
                        (eq this.selectedGoal goal.id)
                        'tio-border-brand tio-bg-brand-soft'
                        'tio-border-default hover:tio-border-brand'
                      }}"
                    {{on "click" (fn this.selectGoal goal.id)}}
                  >
                    <div class="tio-row-top">
                      <div
                        class="p-2 rounded-lg
                          {{if
                            (eq this.selectedGoal goal.id)
                            'tio-bg-brand-soft tio-fg-brand'
                            'tio-bg-sunken tio-fg-secondary'
                          }}"
                      >
                        <goal.icon @size={{24}} />
                      </div>
                      <div>
                        <Text @weight="medium">{{goal.title}}</Text>
                        <Text @size="sm" class="tio-fg-secondary mt-1">{{goal.description}}</Text>
                      </div>
                    </div>
                    {{#if (eq this.selectedGoal goal.id)}}
                      <div class="tio-row-flush mt-3 tio-fg-brand tio-text-sm-medium">
                        <PhCheckCircle @size={{16}} class="mr-1" />
                        Selected
                      </div>
                    {{/if}}
                  </button>
                {{/each}}
              </div>
            </div>
          {{/if}}

          {{#if (eq this.currentStep 2)}}
            <div>
              <Heading @level={{2}} @size="xl" class="mb-2">
                Tell us about your employment
              </Heading>
              <Text class="tio-fg-secondary mb-6">
                Your employer type affects which forgiveness programs you qualify for.
              </Text>

              <div class="space-y-4">
                <div>
                  <Text @weight="medium" class="tio-fg-secondary mb-3">
                    Do you work for a qualifying public service employer?
                  </Text>
                  <Text @size="sm" class="tio-fg-muted mb-4">
                    This includes government (federal, state, local), 501(c)(3) nonprofits, tribal
                    organizations, and some other qualifying organizations.
                  </Text>

                  <div class="tio-flex-loose">
                    <button
                      type="button"
                      class="flex-1 p-4 border-2 rounded-xl transition-all
                        {{if
                          this.isPublicService
                          'tio-strategy-choice--info tio-bg-info-soft'
                          'tio-border-default hover:tio-border-info'
                        }}"
                      {{on "click" (fn this.setPublicService true)}}
                    >
                      <div class="tio-row-tight justify-center">
                        <PhBuildings
                          @size={{24}}
                          class={{if this.isPublicService "tio-fg-info" "tio-fg-muted"}}
                        />
                        <Text @weight="medium">Yes, I work in public service</Text>
                      </div>
                    </button>
                    <button
                      type="button"
                      class="flex-1 p-4 border-2 rounded-xl transition-all
                        {{if
                          this.isPublicService
                          'tio-border-default hover:tio-border-info'
                          'tio-strategy-choice--info tio-bg-info-soft'
                        }}"
                      {{on "click" (fn this.setPublicService false)}}
                    >
                      <div class="tio-row-tight justify-center">
                        <PhBank
                          @size={{24}}
                          class={{if this.isPublicService "tio-fg-muted" "tio-fg-info"}}
                        />
                        <Text @weight="medium">No, private sector</Text>
                      </div>
                    </button>
                  </div>
                </div>

                {{#if this.isPublicService}}
                  {{! No fade-in: `animate-fade-in` is defined in no
                      stylesheet, so this callout has always appeared
                      instantly. }}
                  <div class="mt-6 p-4 tio-bg-success-soft border tio-border-success rounded-xl">
                    <div class="tio-row-tight tio-fg-success-body mb-3">
                      <PhCheckCircle @size={{20}} />
                      <Text @weight="medium">Great! You may qualify for PSLF.</Text>
                    </div>
                    <Text class="tio-fg-secondary mb-4">
                      How many years have you worked in public service since your loans were
                      disbursed?
                    </Text>
                    <div class="tio-wrap-tight">
                      {{#each (array 0 1 2 3 4 5 6 7 8 9 10) as |years|}}
                        <button
                          type="button"
                          class="px-4 py-2 border-2 rounded-lg transition-all
                            {{if
                              (eq this.yearsInPublicService years)
                              'tio-strategy-choice--success tio-bg-success-soft tio-fg-success-body'
                              'tio-border-default hover:tio-border-success'
                            }}"
                          {{on "click" (fn this.setYearsPublicService years)}}
                        >
                          {{#if (eq years 10)}}10+{{else}}{{years}}{{/if}}
                        </button>
                      {{/each}}
                    </div>
                    {{#if (gte this.yearsInPublicService 1)}}
                      <div class="mt-4 p-3 tio-bg-surface rounded-lg">
                        <Text @size="sm" class="tio-fg-secondary">
                          With
                          {{this.yearsInPublicService}}
                          years in public service, you potentially have
                          <strong class="tio-fg-success-body">
                            ~{{this.estimatedQualifyingPayments}}
                            qualifying payments
                          </strong>
                          already. That's
                          {{this.pslfProgressPercent}}% toward PSLF forgiveness!
                        </Text>
                      </div>
                    {{/if}}
                  </div>
                {{/if}}
              </div>
            </div>
          {{/if}}

          {{#if (eq this.currentStep 3)}}
            <div>
              <Heading @level={{2}} @size="xl" class="mb-2">
                Are you considering refinancing?
              </Heading>
              <Text class="tio-fg-secondary mb-6">
                Some lenders advertise lower rates, but there are important tradeoffs.
              </Text>

              <div class="space-y-4">
                <div class="tio-flex-loose">
                  <button
                    type="button"
                    class="flex-1 p-4 border-2 rounded-xl transition-all
                      {{if
                        this.consideringRefinance
                        'tio-strategy-choice--warning tio-bg-warning-soft'
                        'tio-border-default hover:tio-border-strong'
                      }}"
                    {{on "click" (fn this.setConsideringRefinance true)}}
                  >
                    <Text @weight="medium">Yes, I'm considering it</Text>
                  </button>
                  <button
                    type="button"
                    class="flex-1 p-4 border-2 rounded-xl transition-all
                      {{if
                        this.consideringRefinance
                        'tio-border-default hover:tio-border-strong'
                        'tio-strategy-choice--info tio-bg-info-soft'
                      }}"
                    {{on "click" (fn this.setConsideringRefinance false)}}
                  >
                    <Text @weight="medium">No, not right now</Text>
                  </button>
                </div>

                {{#if this.consideringRefinance}}
                  <div class="mt-4 p-5 tio-bg-warning-soft border tio-border-warning rounded-xl">
                    <div class="tio-row-top">
                      <PhWarning @size={{24}} class="tio-fg-warning shrink-0 mt-1" />
                      <div>
                        <Text @weight="semibold" class="tio-fg-warning-h">
                          Important: Refinancing has permanent consequences
                        </Text>
                        <ul class="mt-3 space-y-2 tio-fg-warning-body text-sm">
                          <li class="tio-row-top-tight">
                            <PhX @size={{16}} class="shrink-0 mt-0.5" />
                            <span><strong>Loses PSLF eligibility forever</strong>
                              - Federal loans converted to private cannot qualify</span>
                          </li>
                          <li class="tio-row-top-tight">
                            <PhX @size={{16}} class="shrink-0 mt-0.5" />
                            <span><strong>No IDR plans</strong>
                              - Private loans have no income-driven options</span>
                          </li>
                          <li class="tio-row-top-tight">
                            <PhX @size={{16}} class="shrink-0 mt-0.5" />
                            <span><strong>No federal protections</strong>
                              - No forbearance, deferment, or forgiveness</span>
                          </li>
                          {{#if this.isPublicService}}
                            <li
                              class="tio-row-top-tight p-2 tio-bg-danger-soft rounded-lg tio-fg-danger-body"
                            >
                              <PhProhibit @size={{16}} class="shrink-0 mt-0.5" />
                              <span>
                                <strong>With your public service employment:</strong>
                                You could forfeit ~{{formatCurrency this.pslfForgivenessAmount}}
                                in PSLF forgiveness
                              </span>
                            </li>
                          {{/if}}
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div class="p-4 tio-bg-info-soft border tio-border-info rounded-xl">
                    <div class="tio-row-tight tio-fg-info-body">
                      <PhShield @size={{20}} />
                      <Text @weight="medium">Our commitment: Conflict-free guidance</Text>
                    </div>
                    <Text @size="sm" class="tio-fg-info mt-2">
                      Unlike some platforms, we don't earn referral fees from refinancing companies.
                      Our only goal is helping you make the best decision for your situation.
                    </Text>
                  </div>
                {{/if}}
              </div>
            </div>
          {{/if}}

          {{#if (eq this.currentStep 4)}}
            <div>
              <Heading @level={{2}} @size="xl" class="mb-2">
                Review your profile
              </Heading>
              <Text class="tio-fg-secondary mb-6">
                Here's what we know about your situation. We'll use this to generate personalized
                recommendations.
              </Text>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="p-4 tio-bg-sunken rounded-xl">
                  <Text @weight="medium" class="tio-fg-secondary mb-3 tio-row-tight">
                    <PhBank @size={{18}} />
                    Your Loans
                  </Text>
                  <div class="space-y-2">
                    <div class="tio-flex-between">
                      <Text class="tio-fg-secondary">Total Balance</Text>
                      <Text @weight="semibold">{{formatCurrency
                          this.loanProfile.totalBalance
                        }}</Text>
                    </div>
                    <div class="tio-flex-between">
                      <Text class="tio-fg-secondary">Federal Loans</Text>
                      <Text @weight="semibold">{{formatCurrency
                          this.loanProfile.federalBalance
                        }}</Text>
                    </div>
                    <div class="tio-flex-between">
                      <Text class="tio-fg-secondary">Private Loans</Text>
                      <Text @weight="semibold">{{formatCurrency
                          this.loanProfile.privateBalance
                        }}</Text>
                    </div>
                  </div>
                </div>

                <div class="p-4 tio-bg-sunken rounded-xl">
                  <Text @weight="medium" class="tio-fg-secondary mb-3 tio-row-tight">
                    <PhTarget @size={{18}} />
                    Your Situation
                  </Text>
                  <div class="space-y-2">
                    <div class="tio-flex-between">
                      <Text class="tio-fg-secondary">Goal</Text>
                      <Text @weight="semibold" class="capitalize">
                        {{this.selectedGoal}}
                      </Text>
                    </div>
                    <div class="tio-flex-between">
                      <Text class="tio-fg-secondary">Employment</Text>
                      <Text @weight="semibold">
                        {{if this.isPublicService "Public Service" "Private Sector"}}
                      </Text>
                    </div>
                    {{#if this.isPublicService}}
                      <div class="tio-flex-between">
                        <Text class="tio-fg-secondary">Years in Public Service</Text>
                        <Text @weight="semibold">{{this.yearsInPublicService}}</Text>
                      </div>
                    {{/if}}
                  </div>
                </div>

                <div class="p-4 tio-bg-success-soft rounded-xl md:col-span-2">
                  <Text @weight="medium" class="tio-fg-success-body mb-3 tio-row-tight">
                    <PhSparkle @size={{18}} />
                    Opportunities We Found
                  </Text>
                  <div class="tio-wrap-tight">
                    {{#if this.isPublicService}}
                      <Chip @intent="success" @size="sm">PSLF Eligible</Chip>
                    {{/if}}
                    {{#if this.hasLrapOpportunity}}
                      <Chip @intent="primary" @size="sm">LRAP Programs Available</Chip>
                    {{/if}}
                    {{#if this.scenario.features.employerMatch}}
                      <Chip @intent="default" @size="sm">Employer Contributions</Chip>
                    {{/if}}
                    <Chip @intent="default" @size="sm">IDR Eligible</Chip>
                  </div>
                </div>
              </div>
            </div>
          {{/if}}

          <div class="tio-flex-between mt-8 pt-6 border-t tio-border-default">
            <Button
              @intent="default"
              @appearance="outlined"
              disabled={{eq this.currentStep 1}}
              {{on "click" this.previousStep}}
            >
              Back
            </Button>
            <Button
              @intent="primary"
              @icon={{if (eq this.currentStep 4) PhSparkle PhArrowRight}}
              @iconPlacement="end"
              @label={{if (eq this.currentStep 4) "Get My Strategy" "Continue"}}
              disabled={{and (eq this.currentStep 1) (not this.selectedGoal)}}
              {{on "click" this.nextStep}}
            />
          </div>
        </Card>
      {{/unless}}
      {{#if this.showResults}}

        <div
          class="tio-strategy-results-hero rounded-2xl p-8 tio-fg-inverse relative overflow-hidden"
        >
          <div class="absolute inset-0 opacity-10">
            <div
              class="absolute top-0 right-0 w-64 h-64 tio-bg-surface rounded-full -translate-y-1/2 translate-x-1/2"
            ></div>
            <div
              class="absolute bottom-0 left-0 w-48 h-48 tio-bg-surface rounded-full translate-y-1/2 -translate-x-1/2"
            ></div>
          </div>

          <div class="relative">
            <div class="tio-row-tight mb-4">
              <PhSealCheck @size={{24}} />
              <Text class="tio-strategy-results-copy">Strategy Analysis Complete</Text>
            </div>

            <Heading @level={{1}} class="font-bold tio-fg-inverse mb-2">
              Your Personalized Repayment Strategy
            </Heading>
            <Text class="tio-strategy-results-copy">
              Based on your
              {{if this.isPublicService "public service employment" "employment"}}
              and loan profile, here's what we recommend.
            </Text>

            <Button
              @intent="default"
              @appearance="outlined"
              @size="sm"
              class="tio-strategy-reset"
              {{on "click" this.resetWizard}}
            >
              Start Over
            </Button>
          </div>
        </div>

        {{#each this.criticalWarnings as |warning|}}
          <div
            class="mt-6 p-5 rounded-xl border-2
              {{if
                (eq warning.severity 'critical')
                'tio-bg-danger-soft tio-border-danger'
                (if
                  (eq warning.severity 'warning')
                  'tio-bg-warning-soft tio-border-warning'
                  'tio-bg-info-soft tio-border-info'
                )
              }}"
          >
            <div class="tio-row-top-loose">
              <div
                class="p-2 rounded-lg shrink-0
                  {{if
                    (eq warning.severity 'critical')
                    'tio-bg-danger-soft tio-fg-danger'
                    (if
                      (eq warning.severity 'warning')
                      'tio-bg-warning-soft tio-fg-warning'
                      'tio-bg-info-soft tio-fg-info'
                    )
                  }}"
              >
                <warning.icon @size={{24}} />
              </div>
              <div class="flex-1">
                <Text
                  class="font-semibold
                    {{if
                      (eq warning.severity 'critical')
                      'tio-fg-danger-h'
                      (if (eq warning.severity 'warning') 'tio-fg-warning-h' 'tio-fg-info-h')
                    }}"
                >
                  {{warning.title}}
                </Text>
                <Text
                  class="mt-1
                    {{if
                      (eq warning.severity 'critical')
                      'tio-fg-danger-body'
                      (if (eq warning.severity 'warning') 'tio-fg-warning-body' 'tio-fg-info-body')
                    }}"
                >
                  {{warning.description}}
                </Text>
                {{#if warning.learnMoreLink}}
                  <LinkTo
                    @route={{warning.learnMoreLink}}
                    class="tio-inline-row gap-1 mt-2 tio-text-sm-medium
                      {{if
                        (eq warning.severity 'critical')
                        'tio-fg-danger'
                        (if (eq warning.severity 'warning') 'tio-fg-warning' 'tio-fg-info')
                      }}"
                  >
                    Learn More
                    <PhArrowRight @size={{14}} />
                  </LinkTo>
                {{/if}}
              </div>
            </div>
          </div>
        {{/each}}

        <Card @padding="lg" class="mt-6">
          <Heading @level={{2}} @size="lg" class="mb-4 tio-row-tight">
            <PhInfo @size={{20}} class="tio-fg-muted" />
            Your Profile
          </Heading>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
            {{#each this.strategyFactors as |factor|}}
              <div class="p-3 tio-surface-sunken">
                <div class="tio-row-tight mb-1">
                  <factor.icon
                    @size={{16}}
                    class={{if
                      (eq factor.impact "positive")
                      "tio-fg-success"
                      (if (eq factor.impact "negative") "tio-fg-warning" "tio-fg-muted")
                    }}
                  />
                  <Text @size="xs" class="tio-fg-muted">{{factor.label}}</Text>
                </div>
                <Text @size="sm" @weight="medium">{{factor.value}}</Text>
              </div>
            {{/each}}
          </div>
        </Card>

        <div class="mt-6">
          <Heading @level={{2}} @size="lg" class="mb-4 tio-row-tight">
            <PhCompass @size={{20}} class="tio-fg-info" />
            Available Strategies
          </Heading>

          <div class="space-y-4">
            {{#each this.strategyPaths as |path|}}
              <div
                class="tio-bg-surface rounded-xl border-2 overflow-hidden transition-all
                  {{if path.isRecommended 'tio-strategy-path--recommended' 'tio-border-default'}}"
              >
                {{#if path.isRecommended}}
                  <div class="tio-strategy-path-banner tio-fg-inverse px-4 py-2 tio-row-tight">
                    <PhSparkle @size={{18}} />
                    <Text @size="sm" @weight="medium">Recommended for You</Text>
                  </div>
                {{/if}}

                <div class="p-6">
                  <div class="tio-row-top-loose">
                    <div
                      class="p-3 rounded-xl shrink-0
                        {{if
                          path.isRecommended
                          'tio-bg-success-soft tio-fg-success'
                          'tio-bg-sunken tio-fg-secondary'
                        }}"
                    >
                      <path.icon @size={{28}} />
                    </div>

                    <div class="tio-fill-min">
                      <div class="tio-row-tight flex-wrap">
                        <Heading @level={{3}}>
                          {{path.name}}
                        </Heading>
                        {{#if (eq path.id "refinance")}}
                          <Chip @intent="warning" @size="sm">Not Recommended</Chip>
                        {{/if}}
                      </div>
                      <Text class="tio-fg-secondary mt-1">{{path.description}}</Text>

                      {{#if path.reason}}
                        <div
                          class="mt-3 p-3 tio-bg-success-soft rounded-lg tio-fg-success-body text-sm tio-row-top-tight"
                        >
                          <PhLightbulb @size={{16}} class="shrink-0 mt-0.5" />
                          {{path.reason}}
                        </div>
                      {{/if}}

                      <div
                        class="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 p-4 tio-surface-sunken"
                      >
                        <div>
                          <Text @size="xs" class="tio-fg-muted">Monthly Payment</Text>
                          <Text @weight="bold">{{formatCurrency path.monthlyPayment}}</Text>
                        </div>
                        <div>
                          <Text @size="xs" class="tio-fg-muted">Time to Freedom</Text>
                          <Text @weight="bold">{{path.timeToFreedom}}</Text>
                        </div>
                        <div>
                          <Text @size="xs" class="tio-fg-muted">Total Out-of-Pocket</Text>
                          <Text @weight="bold">{{formatCurrency path.outOfPocket}}</Text>
                        </div>
                        <div>
                          <Text @size="xs" class="tio-fg-muted">Forgiveness</Text>
                          <Text
                            class="font-bold
                              {{if
                                (gte path.forgivenessAmount 1)
                                'tio-fg-success'
                                'tio-fg-primary'
                              }}"
                          >
                            {{#if (gte path.forgivenessAmount 1)}}
                              {{formatCurrency path.forgivenessAmount}}
                            {{else}}
                              $0
                            {{/if}}
                          </Text>
                        </div>
                      </div>

                      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                          <Text
                            @size="sm"
                            @weight="medium"
                            class="tio-fg-secondary mb-2"
                          >Benefits</Text>
                          <ul class="space-y-1">
                            {{#each path.benefits as |benefit|}}
                              <li class="tio-row-top-tight text-sm tio-fg-secondary">
                                <PhCheckCircle
                                  @size={{14}}
                                  class="tio-fg-success shrink-0 mt-0.5"
                                />
                                {{benefit}}
                              </li>
                            {{/each}}
                          </ul>
                        </div>
                        {{#if path.warnings.length}}
                          <div>
                            <Text
                              @size="sm"
                              @weight="medium"
                              class="tio-fg-secondary mb-2"
                            >Considerations</Text>
                            <ul class="space-y-1">
                              {{#each path.warnings as |warning|}}
                                <li class="tio-row-top-tight text-sm tio-fg-warning-body">
                                  <PhWarning @size={{14}} class="tio-fg-warning shrink-0 mt-0.5" />
                                  {{warning}}
                                </li>
                              {{/each}}
                            </ul>
                          </div>
                        {{/if}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            {{/each}}
          </div>
        </div>

        <div class="mt-6 tio-bg-info-soft rounded-xl p-6 border tio-border-info">
          <Heading @level={{2}} @size="lg" class="tio-fg-info-h mb-4 tio-row-tight">
            <PhRocket @size={{20}} />
            Next Steps
          </Heading>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            {{#if this.isPublicService}}
              <LinkTo
                @route="authenticated.reimagined.pslf-vault"
                class="tio-strategy-next-step p-4 tio-bg-surface rounded-lg border tio-border-info transition-all group"
              >
                <PhShield @size={{24}} class="tio-fg-info mb-2" />
                <Text @weight="medium">Secure Your PSLF</Text>
                <Text @size="sm" class="tio-fg-secondary mt-1">
                  Upload ECF and track your qualifying payments
                </Text>
                <div
                  class="tio-row-extra-tight tio-fg-info text-sm mt-2 group-hover:translate-x-1 transition-transform"
                >
                  Open PSLF Vault
                  <PhArrowRight @size={{14}} />
                </div>
              </LinkTo>
            {{/if}}

            <LinkTo
              @route="authenticated.reimagined.finance.optimizer"
              class="tio-strategy-next-step p-4 tio-bg-surface rounded-lg border tio-border-info transition-all group"
            >
              <PhScales @size={{24}} class="tio-fg-success mb-2" />
              <Text @weight="medium">Optimize Section 127</Text>
              <Text @size="sm" class="tio-fg-secondary mt-1">
                Maximize your employer benefit allocation
              </Text>
              <div
                class="tio-row-extra-tight tio-fg-success text-sm mt-2 group-hover:translate-x-1 transition-transform"
              >
                Open Optimizer
                <PhArrowRight @size={{14}} />
              </div>
            </LinkTo>

            <LinkTo
              @route="authenticated.reimagined.loans"
              class="tio-strategy-next-step p-4 tio-bg-surface rounded-lg border tio-border-info transition-all group"
            >
              <PhChartLineUp @size={{24}} class="tio-fg-info mb-2" />
              <Text @weight="medium">View Your Loans</Text>
              <Text @size="sm" class="tio-fg-secondary mt-1">Track progress and manage your accounts</Text>
              <div
                class="tio-row-extra-tight tio-fg-info text-sm mt-2 group-hover:translate-x-1 transition-transform"
              >
                Go to Dashboard
                <PhArrowRight @size={{14}} />
              </div>
            </LinkTo>
          </div>
        </div>
      {{/if}}
    </VStack>
  </template>
}
